import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { Button } from './Button';

interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg';
}

/**
 * A dialog with the accessibility behaviour users expect: Escape closes it,
 * focus moves inside on open, and background scrolling is suppressed so the page
 * behind does not drift while a form is open.
 */
export function Modal({ open, title, description, onClose, children, footer, size = 'md' }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    /*
     * Compensate for the scrollbar that hiding overflow removes. Without this
     * the page behind visibly jumps sideways as the dialog opens.
     */
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    panelRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  /*
   * Rendered into the body rather than in place. The routed <main> animates its
   * opacity, which makes it a stacking context, so a dialog left inside it can
   * never paint above the sticky header no matter how high its z-index is — the
   * header's Working Context row used to cut off the dialog's title.
   */
  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain">
      {/* Clicking the backdrop dismisses; it is presentational, so it is not a button. */}
      <div
        className="animate-fade-in fixed inset-0 backdrop-blur-[2px]"
        style={{ backgroundColor: 'var(--sms-overlay)' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* A min-height flex wrapper, not a centred overflow container: centring
          the panel directly would push the top of a tall dialog above the
          scrollable area, out of reach. */}
      <div className="flex min-h-full items-start justify-center p-4 sm:items-center">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          className={clsx(
            'animate-pop relative z-10 flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded-2xl border border-line bg-card shadow-lg outline-none',
            size === 'lg' ? 'max-w-3xl' : 'max-w-lg',
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div>
              <h2 className="text-base font-semibold text-ink">{title}</h2>
              {description !== undefined && <p className="mt-0.5 text-sm text-muted">{description}</p>}
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close dialog">
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>

          {/* The body scrolls, so the title and the actions stay visible on a
              long form or a short window. */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer !== undefined && (
            <div className="flex shrink-0 justify-end gap-2 rounded-b-2xl border-t border-line bg-surface-2 px-5 py-4">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation for an action that cannot be undone.
 *
 * Used for deletions and deactivations only. Asking "are you sure?" for
 * reversible actions trains people to click through without reading, which
 * makes the prompt useless exactly when it matters.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  destructive = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted">{message}</p>
    </Modal>
  );
}
